import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { useCachedState, useRefreshVersion } from './store';
import { Badge, Empty, ErrorState, Field, Loading, Metric, Select, dayOnly, money, shortNumber, titleCase, useCan, useToast } from './ui';

const RANGES = [['7', 'Last 7 days'], ['30', 'Last 30 days'], ['90', 'Last 90 days'], ['365', 'Last year']];
const MANUAL_PAGE_SIZE = 50;
const KIND_LABELS = { tts: 'voice', script: 'script writer' };

const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Estimated against real.
 *
 * The estimate is what the pipeline thinks each request cost, from the
 * provider's price list at the time. The real figure is what the invoice
 * said, typed in here per provider and period. A monthly bill is spread
 * across the days it covers, so any range compares like with like.
 */
export function Costs() {
  const can = useCan();
  const [range, setRange] = useState('30');
  const defaultFrom = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const defaultTo = new Date().toISOString();
  const [summary, setSummary] = useCachedState(`/costs/summary?from=${defaultFrom}&to=${defaultTo}`);
  const [manual, setManual] = useCachedState(`/costs/manual?limit=${MANUAL_PAGE_SIZE}&offset=0`);
  const [manualPage, setManualPage] = useState(0);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [toast, toastNode] = useToast();
  const refreshVersion = useRefreshVersion();

  const load = useCallback(async () => {
    setError('');
    const from = new Date(Date.now() - Number(range) * 24 * 60 * 60 * 1000).toISOString();
    const to = new Date().toISOString();
    try {
      const [s, m] = await Promise.all([
        api(`/costs/summary?from=${from}&to=${to}`),
        api(`/costs/manual?limit=${MANUAL_PAGE_SIZE}&offset=${manualPage * MANUAL_PAGE_SIZE}`),
      ]);
      setSummary(s); setManual(m);
    } catch (caught) { setError(caught.message); }
  }, [range, manualPage, refreshVersion]);
  useEffect(() => { void load(); }, [load]);

  const estimated = useMemo(() => (summary?.providers ?? []).reduce((sum, p) => sum + Number(p.estimated_cost_usd), 0), [summary]);
  const real = useMemo(() => (summary?.manual ?? []).reduce((sum, m) => sum + Number(m.amount_usd), 0), [summary]);
  const byKind = useMemo(() => (summary?.providers ?? []).reduce((acc, p) => { acc[p.kind] = (acc[p.kind] ?? 0) + Number(p.estimated_cost_usd); return acc; }, {}), [summary]);
  const realByProvider = useMemo(() => Object.fromEntries((summary?.manual ?? []).map((m) => [m.provider, Number(m.amount_usd)])), [summary]);
  const gap = real - estimated;

  const remove = async (entry) => {
    if (!window.confirm(`Delete the ${money(entry.amount_usd)} ${entry.provider} entry for ${dayOnly(entry.period_start)} – ${dayOnly(entry.period_end)}?`)) return;
    try { await api(`/costs/manual/${entry.id}`, { method: 'DELETE' }); toast('Entry deleted'); await load(); }
    catch (caught) { toast(caught.message, 'danger'); }
  };

  if (error) return <ErrorState message={error} retry={load} />;
  if (!summary || !manual) return <Loading />;

  return <>
    <section className="page-title"><div><div className="eyebrow">Costs</div><h1>Costs</h1><p>Estimated spend per request, next to what the invoices actually said.</p></div>
      <div className="btn-row"><Select value={range} onChange={(e) => setRange(e.target.value)} options={RANGES} label="Date range" />{can('manage_costs') && <button className="primary" onClick={() => setAdding(true)}>+ Add real cost</button>}</div></section>

    <section className="metric-grid">
      <Metric label="Estimated" value={money(estimated)} detail={`Render ${money(byKind.render)} · Transcription ${money(byKind.transcription)} · Voice ${money(byKind.tts)} · Script ${money(byKind.script)}`} />
      <Metric label="Real (from invoices)" value={real ? money(real) : '—'} detail={real ? `${summary.manual.length} provider${summary.manual.length === 1 ? '' : 's'} entered for this range` : 'Add invoices to compare'} />
      <Metric label="Gap" value={real ? `${gap >= 0 ? '+' : '−'}${money(Math.abs(gap))}` : '—'} detail={real ? (gap >= 0 ? 'Real spend above the estimate' : 'Estimate above real spend') : 'Real minus estimated'} alert={real > 0 && Math.abs(gap) > Math.max(5, estimated * 0.25)} />
      <Metric label="Provider routes" value={summary.providers.length} detail="Provider and workload combinations" />
    </section>

    <article className="panel table-panel"><div className="panel-heading"><div><span className="eyebrow">BY PROVIDER</span><h2>Estimated vs real, {RANGES.find(([v]) => v === range)?.[1].toLowerCase()}</h2></div></div>
      <div className="table-wrap"><table><thead><tr><th>Provider</th><th>Workload</th><th>Attempts</th><th>Success</th><th>Volume</th><th>Estimated</th><th>Real</th></tr></thead><tbody>
        {summary.providers.length ? summary.providers.map((p, index) => <tr key={`${p.provider}-${p.kind}-${index}`}>
          <td><b>{p.provider}</b></td><td><Badge>{KIND_LABELS[p.kind] ?? p.kind}</Badge></td><td>{p.attempts}</td>
          <td>{p.attempts ? `${Math.round((p.succeeded / p.attempts) * 100)}%` : '—'}</td>
          <td>{p.kind === 'tts' ? `${shortNumber(p.units)} chars` : p.kind === 'script' ? `${shortNumber(p.units)} tokens` : Number(p.input_seconds) ? `${Math.round(Number(p.input_seconds) / 60)} min` : '—'}</td>
          <td><b>{money(p.estimated_cost_usd)}</b></td>
          <td>{realByProvider[p.provider] != null ? <b>{money(realByProvider[p.provider])}</b> : <span className="unassigned">—</span>}</td>
        </tr>) : <tr><td colSpan="7"><Empty title="No cost events in this period" body="The cost ledger starts filling with the next pipeline runs." /></td></tr>}
        {summary.manual.filter((m) => !summary.providers.some((p) => p.provider === m.provider)).map((m) => <tr key={`manual-${m.provider}`}>
          <td><b>{m.provider}</b></td><td><Badge tone="muted">invoice only</Badge></td><td>—</td><td>—</td><td>—</td><td><span className="unassigned">—</span></td><td><b>{money(m.amount_usd)}</b></td>
        </tr>)}
      </tbody></table></div></article>

    <article className="panel table-panel"><div className="panel-heading"><div><span className="eyebrow">REAL COSTS</span><h2>Invoices entered</h2></div>{can('manage_costs') && <button className="text-button" onClick={() => setAdding(true)}>+ Add</button>}</div>
      <div className="table-wrap"><table><thead><tr><th>Provider</th><th>What</th><th>Period</th><th>Amount</th><th>Entered by</th>{can('manage_costs') && <th />}</tr></thead><tbody>
        {manual.items.length ? manual.items.map((m) => <tr key={m.id}>
          <td><b>{m.provider}</b></td>
          <td>{m.label || <span className="unassigned">—</span>}{m.note && <small className="plain">{m.note}</small>}</td>
          <td>{dayOnly(m.period_start)} – {dayOnly(m.period_end)}</td>
          <td><b>{money(m.amount_usd)}</b></td>
          <td>{m.created_by_name || m.created_by_email || '—'}<small>{dayOnly(m.created_at)}</small></td>
          {can('manage_costs') && <td className="row-actions"><button className="text-button danger" onClick={() => remove(m)}>Delete</button></td>}
        </tr>) : <tr><td colSpan="6"><Empty title="No invoices entered yet" body="Add the AWS, Google Cloud, Groq or any other bill to see how the estimate holds up." /></td></tr>}
      </tbody></table></div></article>

    {(manual.page?.total ?? 0) > MANUAL_PAGE_SIZE && <div className="pager admin-list-pager"><button className="subtle" disabled={manualPage === 0} onClick={() => setManualPage((p) => p - 1)}>← Prev</button><span>Page {manualPage + 1} of {Math.ceil(manual.page.total / MANUAL_PAGE_SIZE)}</span><button className="subtle" disabled={(manualPage + 1) * MANUAL_PAGE_SIZE >= manual.page.total} onClick={() => setManualPage((p) => p + 1)}>Next →</button></div>}

    {adding && <AddCost providers={manual.providers} onClose={() => setAdding(false)} onSaved={async () => { setAdding(false); toast('Real cost added'); await load(); }} onError={(m) => toast(m, 'danger')} />}
    {toastNode}
  </>;
}

function AddCost({ providers, onClose, onSaved, onError }) {
  const [form, setForm] = useState({ provider: 'aws', custom: '', label: '', amount: '', periodStart: firstOfMonth(), periodEnd: today(), note: '' });
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const provider = form.provider === 'other' ? form.custom.trim().toLowerCase() : form.provider;
  const valid = provider.length >= 2 && Number(form.amount) >= 0 && form.amount !== '' && form.periodStart && form.periodEnd && form.periodEnd >= form.periodStart;

  const submit = async (event) => {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      await api('/costs/manual', { method: 'POST', body: JSON.stringify({
        provider, label: form.label.trim() || undefined, amountUsd: Number(form.amount),
        periodStart: form.periodStart, periodEnd: form.periodEnd, note: form.note.trim() || undefined,
      }) });
      await onSaved();
    } catch (caught) { onError(caught.message); } finally { setBusy(false); }
  };

  return <div className="modal-backdrop" onClick={onClose}><form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
    <div className="panel-heading"><div><span className="eyebrow">REAL COST</span><h2>Add an invoice</h2></div><button type="button" className="icon-button" onClick={onClose}>✕</button></div>
    <div className="form-stack">
      <div className="field-row">
        <Field label="Provider"><Select value={form.provider} onChange={set('provider')} options={providers.map((p) => [p, p === 'aws' ? 'AWS' : p === 'gcp' ? 'Google Cloud' : titleCase(p)])} /></Field>
        {form.provider === 'other' ? <Field label="Name"><input value={form.custom} onChange={set('custom')} placeholder="e.g. cloudflare" /></Field> : <Field label="What (optional)"><input value={form.label} onChange={set('label')} placeholder="e.g. Lambda + S3" /></Field>}
      </div>
      {form.provider === 'other' && <Field label="What (optional)"><input value={form.label} onChange={set('label')} placeholder="e.g. CDN bandwidth" /></Field>}
      <div className="field-row">
        <Field label="Amount (USD)"><input type="number" min="0" step="0.01" value={form.amount} onChange={set('amount')} placeholder="0.00" /></Field>
        <div />
      </div>
      <div className="field-row">
        <Field label="Period from"><input type="date" value={form.periodStart} max={form.periodEnd} onChange={set('periodStart')} /></Field>
        <Field label="to"><input type="date" value={form.periodEnd} min={form.periodStart} onChange={set('periodEnd')} /></Field>
      </div>
      <Field label="Note (optional)" hint="Invoice number, what it covered, anything the next person needs."><textarea rows={3} value={form.note} onChange={set('note')} /></Field>
    </div>
    <div className="drawer-foot"><button type="button" className="subtle" onClick={onClose}>Cancel</button><button className="primary" disabled={!valid || busy}>{busy ? 'Saving…' : 'Add cost'}</button></div>
  </form></div>;
}
